import React, { useEffect, useMemo, useState } from "react";
import { X, Calendar, Truck, FileText, QrCode, Tag, BadgeInfo } from "lucide-react";
import { supabase } from "../../../utils/supabase";
import { getInstrument } from "../../../utils/api";
import { printRemitoSoloTexto } from "../../../utils/api-services";

function formatDate(value) {
  if (!value) return "-";
  const s = String(value);
  const ymd = s.includes("T") ? s.slice(0, 10) : s.slice(0, 10);
  if (!ymd) return "-";
  const [y, m, d] = ymd.split("-");
  if (!y || !m || !d) return "-";
  return `${d}/${m}/${y}`;
}

function fieldValue(value) {
  if (value == null) return "-";
  const s = String(value).trim();
  return s || "-";
}

function getStatusLabel(status) {
  const map = {
    pending_reception: "Pendiente de recepción",
    active: "En depósito",
    in_progress: "En proceso",
    paused: "Pausado",
    calibrated: "Calibrado",
    rejected: "Rechazado",
    with_certificate: "Con certificado",
    without_certificate: "Sin certificado",
    reviewed: "Revisado por jefe",
    ready_to_remit: "Listo para remitar",
    remitted: "Remitido",
    delivered: "Entregado",
    delivered_with_certificate: "Entregado con certificado",
    delivered_without_certificate: "Entregado sin certificado",
    delivered_not_calibrated: "Entregado no calibrado",
    delivered_not_repaired: "Entregado no reparado",
    delivered_repaired: "Entregado reparado",
    cancelled: "Cancelado",
  };
  return map[status] || status || "-";
}

const LAB_NAME = "Laboratorio AIRPI";

export default function InstrumentDetailModal({ open, instrumentId, onClose }) {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [instrument, setInstrument] = useState(null);
  const [movementInfo, setMovementInfo] = useState(null);
  const [openingRemito, setOpeningRemito] = useState(false);

  useEffect(() => {
    if (!open || !instrumentId) {
      setInstrument(null);
      setMovementInfo(null);
      setErrorMsg("");
      setLoading(false);
      setOpeningRemito(false);
      return;
    }

    let active = true;

    (async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const [inst, latestServiceOrderResponse] = await Promise.all([
          getInstrument(instrumentId),
          supabase
            .from("service_orders")
            .select("id, po_id, code, status, reception_date, created_at")
            .eq("instrument_id", instrumentId)
            .order("created_at", { ascending: false })
            .limit(1),
        ]);

        if (!active) return;

        setInstrument(inst);

        if (latestServiceOrderResponse.error) throw latestServiceOrderResponse.error;
        const latestServiceOrder = latestServiceOrderResponse.data?.[0] || null;

        let currentServiceOrderIds = latestServiceOrder?.id ? [latestServiceOrder.id] : [];
        if (latestServiceOrder?.po_id) {
          const { data: siblingRows, error: siblingError } = await supabase
            .from("service_orders")
            .select("id")
            .eq("po_id", latestServiceOrder.po_id);
          if (siblingError) throw siblingError;
          currentServiceOrderIds = (siblingRows || []).map((row) => row.id).filter(Boolean);
        }

        const remitoRows = await supabase
          .from("remito_items")
          .select("remito_id, remitos(id, os_id, code, official_number, date_out, status)")
          .eq("instrument_id", instrumentId);

        if (remitoRows.error) throw remitoRows.error;

        const currentServiceOrderSet = new Set(currentServiceOrderIds.map(String));
        const currentRemitos = (remitoRows.data || [])
          .map((row) => row.remitos)
          .filter((remito) =>
            remito &&
            (!currentServiceOrderSet.size || currentServiceOrderSet.has(String(remito.os_id)))
          )
          .sort((a, b) => String(b.date_out || "").localeCompare(String(a.date_out || "")));

        setMovementInfo({
          latestServiceOrder,
          latestRemito: currentRemitos[0] || null,
          count: currentRemitos.length,
        });
      } catch (e) {
        console.error("[InstrumentDetailModal] load error:", e);
        setErrorMsg(e?.message || "No se pudo cargar la ficha.");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [open, instrumentId]);

  async function handleOpenLatestRemito() {
    if (!movementInfo?.latestRemito?.id || openingRemito) return;
    try {
      setOpeningRemito(true);
      await printRemitoSoloTexto(movementInfo.latestRemito.id, {});
    } catch (e) {
      console.error("[InstrumentDetailModal] open remito error:", e);
      setErrorMsg(e?.message || "No se pudo abrir el remito.");
    } finally {
      setOpeningRemito(false);
    }
  }

  const title = useMemo(() => {
    if (!instrument) return "Ficha de instrumento";
    return [instrument.brand, instrument.model].filter(Boolean).join(" ") || instrument.tag || "Instrumento";
  }, [instrument]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 grid place-items-center p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        className="w-full max-w-6xl max-h-[92vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0F1115] text-white shadow-2xl flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h3 className="text-lg font-semibold">{title}</h3>
            <p className="text-xs text-white/60">
              {instrument
                ? `${LAB_NAME} - Lab ID ${instrument.lab_id || "-"} - ${instrument.tag || "-"}`
                : "Cargando ficha..."}
            </p>
          </div>
          <button
            className="inline-flex items-center gap-2 rounded-md border border-white/10 px-3 py-1.5 hover:bg-white/10"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
            Cerrar
          </button>
        </div>
        <div className="px-5 py-2 text-[11px] uppercase tracking-[0.25em] text-white/45 border-b border-white/5">
          Laboratorio AIRPI
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-sm text-white/70">Cargando información del instrumento...</div>
          ) : errorMsg ? (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">
              {errorMsg}
            </div>
          ) : instrument ? (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <Card title="Cliente" value={instrument.client?.name} icon={<Tag className="h-4 w-4" />} />
                <Card title="Estado" value={getStatusLabel(instrument.status)} icon={<BadgeInfo className="h-4 w-4" />} />
                <Card title="Ingreso actual" value={formatDate(movementInfo?.latestServiceOrder?.reception_date || movementInfo?.latestServiceOrder?.created_at || instrument.reception_date)} icon={<Calendar className="h-4 w-4" />} />
                <Card title="Salida actual" value={formatDate(movementInfo?.latestRemito?.date_out)} icon={<Truck className="h-4 w-4" />} />
              </div>

              <Section title="Identificación" icon={<Tag className="h-4 w-4" />}>
                <Grid>
                  <Field label="Lab ID" value={fieldValue(instrument.lab_id != null ? `#${instrument.lab_id}` : null)} />
                  <Field label="Tag" value={fieldValue(instrument.tag)} />
                  <Field label="Marca" value={fieldValue(instrument.brand)} />
                  <Field label="Modelo" value={fieldValue(instrument.model)} />
                  <Field label="Serie" value={fieldValue(instrument.serial)} />
                  <Field label="Tipo" value={fieldValue(instrument.type)} />
                  <Field label="Categoría" value={fieldValue(instrument.category?.name || instrument.category_name)} />
                  <Field label="Baker" value={fieldValue(instrument.baker_code)} />
                </Grid>
              </Section>

              <Section title="Especificaciones" icon={<FileText className="h-4 w-4" />}>
                <Grid>
                  <Field label="Unidad" value={fieldValue(instrument.unit)} />
                  <Field label="Rango mínimo" value={fieldValue(instrument.range_min)} />
                  <Field label="Rango máximo" value={fieldValue(instrument.range_max)} />
                  <Field label="Resolución" value={fieldValue(instrument.resolution)} />
                  <Field label="Tolerancia" value={fieldValue(instrument.tolerance)} />
                  <Field label="Instrumento analógico" value={instrument.is_analog ? "Sí" : "No"} />
                  <Field label="Intervalo de recalibración" value={fieldValue(instrument.calibrationintervalmonths)} />
                  <Field label="Lugar de calibración" value={fieldValue(instrument.calibration_place)} />
                </Grid>
              </Section>

              <Section title="Fechas y trazabilidad" icon={<Calendar className="h-4 w-4" />}>
                <Grid>
                  <Field label="Ingreso de la OS actual" value={formatDate(movementInfo?.latestServiceOrder?.reception_date || movementInfo?.latestServiceOrder?.created_at || instrument.reception_date)} />
                  <Field label="Salida de la OS actual" value={formatDate(movementInfo?.latestRemito?.date_out)} />
                  <Field label="OS actual" value={fieldValue(movementInfo?.latestServiceOrder?.code)} />
                  <Field label="Última calibración" value={formatDate(instrument.lastcalibrationdate)} />
                  <Field label="Próxima calibración" value={formatDate(instrument.nextcalibrationdate)} />
                  <Field label="N° certificado" value={fieldValue(instrument.certificatenumber)} />
                  <Field label="Estado físico" value={fieldValue(instrument.status)} />
                </Grid>
              </Section>

              <Section title="Estado en laboratorio" icon={<BadgeInfo className="h-4 w-4" />}>
                <Grid>
                  <Field label="Bajada al lab" value={formatDate(instrument.lab_entry_date)} />
                  <Field label="Revisado por jefe" value={instrument.reviewed_at ? formatDate(instrument.reviewed_at) : null} />
                  <Field label="¿Está roto?" value={instrument.is_broken === true ? "Sí" : instrument.is_broken === false ? "No" : null} />
                  <Field label="Reparación solicitada" value={instrument.repair_requested === true ? "Sí" : instrument.repair_requested === false ? "No" : null} />
                </Grid>
                {instrument.rejection_reason && (
                  <div className="mt-3 rounded-lg border border-red-500/20 bg-red-500/10 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-red-400 mb-1">Motivo de rechazo</div>
                    <div className="text-sm text-red-100 whitespace-pre-wrap">{instrument.rejection_reason}</div>
                  </div>
                )}
                {instrument.technician_notes && (
                  <div className="mt-3 rounded-lg border border-amber-500/20 bg-amber-500/10 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-amber-400 mb-1">Observaciones del técnico</div>
                    <div className="text-sm text-amber-100 whitespace-pre-wrap">{instrument.technician_notes}</div>
                  </div>
                )}
              </Section>

              <Section title="Movimiento de laboratorio" icon={<Truck className="h-4 w-4" />}>
                {movementInfo?.latestRemito ? (
                  <div className="space-y-3">
                    <Grid>
                      <Field label="Ultimo remito" value={fieldValue(movementInfo.latestRemito.official_number || movementInfo.latestRemito.code)} />
                      <Field label="Fecha del remito" value={formatDate(movementInfo.latestRemito.date_out)} />
                      <Field label="Estado del remito" value={fieldValue(movementInfo.latestRemito.status)} />
                      <Field label="Remitos de la OS actual" value={fieldValue(movementInfo.count)} />
                    </Grid>
                    <div>
                      <button
                        type="button"
                        onClick={handleOpenLatestRemito}
                        disabled={openingRemito}
                        className="inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/10 px-3 py-2 text-sm hover:bg-white/15 disabled:opacity-60"
                      >
                        <Truck className="h-4 w-4" />
                        {openingRemito ? "Abriendo remito..." : "Ver remito"}
                      </button>
                    </div>
                  </div>
                ) : (
                    <div className="text-sm text-white/60">Este instrumento todavía no tiene remito asociado.</div>
                  )}
              </Section>

              <Section title="Observaciones" icon={<FileText className="h-4 w-4" />}>
                <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-sm whitespace-pre-wrap">
                  {instrument.observations ? instrument.observations : "Sin observaciones."}
                </div>
              </Section>

              <Section title="QR y archivos" icon={<QrCode className="h-4 w-4" />}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <ActionCard
                    label="QR imagen"
                    description={instrument.qr_image_url ? "Abrir imagen del QR" : "No hay imagen de QR cargada."}
                    actionLabel="Abrir QR"
                    onClick={instrument.qr_image_url ? () => window.open(instrument.qr_image_url, "_blank", "noopener,noreferrer") : undefined}
                    disabled={!instrument.qr_image_url}
                  />
                  <ActionCard
                    label="QR PDF"
                    description={instrument.qr_pdf_url ? "Abrir PDF del QR" : "No hay PDF de QR cargado."}
                    actionLabel="Abrir PDF"
                    onClick={instrument.qr_pdf_url ? () => window.open(instrument.qr_pdf_url, "_blank", "noopener,noreferrer") : undefined}
                    disabled={!instrument.qr_pdf_url}
                  />
                </div>
              </Section>
            </div>
          ) : (
            <div className="text-sm text-white/60">No hay informacion para mostrar.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <section className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
        {icon}
        {title}
      </div>
      {children}
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">{children}</div>;
}

function Field({ label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/20 p-3">
      <div className="text-[11px] uppercase tracking-wide text-white/50">{label}</div>
      <div className="mt-1 text-sm break-words">{value || "—"}</div>
    </div>
  );
}

function Card({ title, value, icon }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wide text-white/50">
        {icon}
        {title}
      </div>
      <div className="text-base font-semibold">{value || "-"}</div>
    </div>
  );
}

function ActionCard({ label, description, actionLabel, onClick, disabled }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/20 p-3">
      <div className="text-[11px] uppercase tracking-wide text-white/50">{label}</div>
      <div className="mt-1 text-sm text-white/80">{description}</div>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="mt-3 inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/10 px-3 py-2 text-sm hover:bg-white/15 disabled:opacity-50"
      >
        {actionLabel}
      </button>
    </div>
  );
}
